import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { toast } from "sonner";
import { api } from "../../App";

export default function CommentsSection({ applicationId, comments, onCommentAdded }) {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState("");
  // Track which comments are expanded
  const [expandedComments, setExpandedComments] = useState({});

  // Update expanded state when comments change
  useEffect(() => {
    if (comments.length > 2) {
      // Only first comment expanded by default when more than 2 comments
      const initialState = {};
      comments.forEach((comment, index) => {
        initialState[comment.id] = index === 0; // Only first one is expanded
      });
      setExpandedComments(initialState);
    } else {
      // If 2 or fewer comments, all are expanded
      const initialState = {};
      comments.forEach((comment) => {
        initialState[comment.id] = true;
      });
      setExpandedComments(initialState);
    }
  }, [comments.length]); // Only re-run when number of comments changes

  const toggleComment = (commentId) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await api.post(`/applications/${applicationId}/comments`, { text: newComment });
      setNewComment("");
      toast.success(t("common.commentAdded"));
      onCommentAdded();
    } catch (error) {
      toast.error(t("common.failedToAddComment"));
    }
  };

  // Check if we need collapsible behavior (more than 2 comments)
  const isCollapsible = comments.length > 2;

  return (
    <Card className="p-6 bg-white border border-stone-200 shadow-sm">
      <h2 className="text-xl font-manrope font-bold text-emerald-700 mb-6">
        {t("comments.title")} {comments.length > 0 && `(${comments.length})`}
      </h2>

      <div className="space-y-3 mb-6" data-testid="comments-section">
        {comments.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">
            {t("comments.noComments")}
          </p>
        ) : (
          comments.map((comment, index) => {
            const isExpanded = isCollapsible ? expandedComments[comment.id] : true;
            
            return (
              <div
                key={comment.id}
                className="border border-stone-200 rounded-lg overflow-hidden"
                data-testid={`comment-${comment.id}`}
              >
                {/* Comment Header - Clickable when collapsible */}
                <div 
                  className={`p-3 bg-stone-50 flex justify-between items-center ${isCollapsible ? 'cursor-pointer hover:bg-stone-100' : ''}`}
                  onClick={() => isCollapsible && toggleComment(comment.id)}
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {comment.user_name}
                      </p>
                      <p className="text-xs text-slate-500 capitalize">
                        {comment.user_role}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-slate-500">
                      {format(new Date(comment.created_at), "MMM dd, yyyy 'at' h:mm a")}
                    </p>
                    {isCollapsible && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleComment(comment.id);
                        }}
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-slate-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-500" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Comment Body - Collapsible */}
                {isExpanded && (
                  <div className="p-4 bg-white border-t border-stone-100">
                    <p className="text-sm text-slate-700 whitespace-pre-line">{comment.text}</p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={handleAddComment} className="space-y-3" data-testid="comment-form">
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={t("comments.placeholder")}
          rows={3}
          data-testid="comment-input"
        />
        <Button
          type="submit"
          className="bg-emerald-800 hover:bg-emerald-900 text-white"
          data-testid="add-comment-button"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          {t("comments.addComment")}
        </Button>
      </form>
    </Card>
  );
}
